// Communication Service API
// Integrated with backend API as per specification

import {
  Announcement,
  Message,
  FeeReminder,
  Notification,
  FeeDefaulter,
  CommunicationHistory,
  CommunicationStats,
  CreateAnnouncementRequest,
  SendMessageRequest,
  SendFeeReminderRequest,
  GetNotificationsRequest,
  GetNotificationsResponse,
  UnreadCountResponse,
  FeeReminderFilters,
  CommunicationFilters,
  NotificationPreferences,
  DeliveryStatus,
  Conversation,
  ChatMessage,
  ChatMessagesResponse,
  SendChatMessageRequest,
  StartConversationRequest,
} from '../../types/communication';

import { axiosInstance } from './axios';

import { ApiResponse, PaginatedResponse } from '../../types/common';
import { CommunicationPerson } from '../../types/communication';

// Mock data — only imported for USE_MOCK_API = true (dev/testing only)
// When USE_MOCK_API = false (production), tree-shaking eliminates all mock paths
import {
  MOCK_ANNOUNCEMENTS,
  MOCK_MESSAGES,
  MOCK_FEE_REMINDERS,
  MOCK_NOTIFICATIONS,
  MOCK_FEE_DEFAULTERS,
  MOCK_CONVERSATIONS,
  MOCK_CHAT_MESSAGES,
  MOCK_PEOPLE,
} from '../../mocks/communicationData';

// Environment check for mock API usage
const USE_MOCK_API = false;

// Delay helper for mock implementations
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const API_BASE_PATH = '/v1';

// ============================================
// ANNOUNCEMENT APIs
// ============================================

export const createAnnouncement = async (
  data: CreateAnnouncementRequest
): Promise<ApiResponse<number>> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   console.log('Creating announcement:', data);
  //   await delay(600);
    
  //   const newAnnouncement: Announcement = {
  //     id: `ann_${Date.now()}`,
  //     title: data.title,
  //     body: data.body,
  //     audience: data.audience,
  //     targetDetail: data.targetIds?.length ? `${data.targetIds.length} selected` : 'All',
  //     targetIds: data.targetIds,
  //     showAsPopup: data.showAsPopup,
  //     createdAt: new Date().toISOString(),
  //     createdBy: {
  //       id: 'user_current',
  //       name: 'Current User',
  //       role: 'Admin',
  //     },
  //     recipientCount: 850,
  //     deliveryStatus: DeliveryStatus.SENT,
  //   };
    
  //   MOCK_ANNOUNCEMENTS.unshift(newAnnouncement);
  //   return newAnnouncement;
  // }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.post<ApiResponse<number>>(
    `${API_BASE_PATH}/announcements/send`,
    {
      title: data.title,
      body: data.body,
      audienceType: data.audience,
      selectedTargets: data.targetIds || [],
      showAsPopup: data.showAsPopup,
      priority: 'normal',
    }
  );
  
  if (!response.data.succeeded) {
    throw new Error(response.data.messages[0] || 'Failed to send announcement');
  }
  
  return response.data;
};

export const getAnnouncements = async (
  filters?: CommunicationFilters
): Promise<Announcement[]> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(400);
    
    let filteredData = [...MOCK_ANNOUNCEMENTS];
    
    if (filters?.status) {
      filteredData = filteredData.filter(a => a.deliveryStatus === filters.status);
    }
    
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filteredData = filteredData.filter(a =>
        a.title.toLowerCase().includes(searchLower) ||
        a.body.toLowerCase().includes(searchLower)
      );
    }
    
    return filteredData;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<{ success: boolean; data: Announcement[]; pagination?: any }>(
    `${API_BASE_PATH}/announcements`,
    { params: filters }
  );
  
  if (!response.data.success) {
    throw new Error('Failed to fetch announcements');
  }
  
  return response.data.data || [];
};

export const getAnnouncementById = async (id: string): Promise<Announcement> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(200);
    
    const announcement = MOCK_ANNOUNCEMENTS.find(a => a.id === id);
    if (!announcement) {
      throw new Error('Announcement not found');
    }
    
    return announcement;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<{ success: boolean; data: Announcement }>(
    `${API_BASE_PATH}/announcements/${id}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error('Announcement not found');
  }
  
  return response.data.data;
};

// ============================================
// MESSAGING APIs
// ============================================

export const sendMessage = async (
  data: SendMessageRequest
): Promise<Message> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(500);
    
    const newMessage: Message = {
      id: `msg_${Date.now()}`,
      recipients: [],
      content: data.content,
      type: data.type,
      showAsPopup: data.showAsPopup,
      sentAt: new Date().toISOString(),
      sentBy: {
        id: 'user_current',
        name: 'Current User',
        role: 'Teacher',
      },
      deliveryStatus: DeliveryStatus.SENT,
      readBy: [],
    };
    
    MOCK_MESSAGES.unshift(newMessage);
    return newMessage;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.post<{ success: boolean; data: Message; message?: string }>(
    `${API_BASE_PATH}/messages/send`,
    {
      recipientId: data.recipientIds?.[0],
      subject: data.content.slice(0, 100),
      body: data.content,
      priority: 'normal',
    }
  );
  
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to send message');
  }
  
  return response.data.data;
};

export const getMessages = async (
  filters?: CommunicationFilters
): Promise<Message[]> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(300);
    return [...MOCK_MESSAGES];
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<{ success: boolean; data: Message[]; pagination?: any }>(
    `${API_BASE_PATH}/messages`,
    { 
      params: {
        ...filters,
        folder: 'inbox',
      }
    }
  );
  
  if (!response.data.success) {
    throw new Error('Failed to fetch messages');
  }
  
  return response.data.data || [];
};

export const markMessageAsRead = async (messageId: string): Promise<void> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(200);
    
    const message = MOCK_MESSAGES.find(m => m.id === messageId);
    if (message) {
      message.deliveryStatus = DeliveryStatus.READ;
    }
    return;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.patch<{ success: boolean }>(
    `${API_BASE_PATH}/messages/${messageId}/read`
  );
  
  if (!response.data.success) {
    throw new Error('Failed to mark message as read');
  }
};

// ============================================
// FEE REMINDER APIs
// ============================================

export const getStudentsWithDues = async (
  filters?: FeeReminderFilters
): Promise<FeeDefaulter[]> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(400);
    
    let filteredData = [...MOCK_FEE_DEFAULTERS];
    
    if (filters?.classIds && filters.classIds.length > 0) {
      filteredData = filteredData.filter(s => 
        filters.classIds!.includes(s.classId)
      );
    }
    
    if (filters?.minDue !== undefined) {
      filteredData = filteredData.filter(s => s.feeBalance >= filters.minDue!);
    }
    
    if (filters?.maxDue !== undefined) {
      filteredData = filteredData.filter(s => s.feeBalance <= filters.maxDue!);
    }
    
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filteredData = filteredData.filter(s =>
        s.studentName.toLowerCase().includes(searchLower) ||
        s.admissionNo.toLowerCase().includes(searchLower) ||
        s.mobile.includes(searchLower)
      );
    }
    
    return filteredData;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<{ success: boolean; data: FeeDefaulter[]; pagination?: any }>(
    `${API_BASE_PATH}/fee-reminders/defaulters`,
    { params: filters }
  );
  
  if (!response.data.success) {
    throw new Error('Failed to fetch fee defaulters');
  }
  
  return response.data.data || [];
};

export const sendFeeReminders = async (
  data: SendFeeReminderRequest
): Promise<FeeReminder> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(600);
    
    const totalDue = data.studentIds.length * 15000;
    
    const newReminder: FeeReminder = {
      id: `fee_${Date.now()}`,
      studentIds: data.studentIds,
      message: data.message,
      customMessage: data.customMessage,
      sentAt: new Date().toISOString(),
      sentBy: {
        id: 'user_current',
        name: 'Current User',
        role: 'Admin',
      },
      totalDue,
      deliveryStatus: DeliveryStatus.SENT,
    };
    
    MOCK_FEE_REMINDERS.unshift(newReminder);
    return newReminder;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.post<{ success: boolean; data: FeeReminder; message?: string }>(
    `${API_BASE_PATH}/fee-reminders/send`,
    {
      studentIds: data.studentIds,
      message: data.message,
      tone: data.customMessage ? 'urgent' : 'polite',
      includeBreakdown: true,
      sendViaEmail: true,
      sendViaSMS: false,
    }
  );
  
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to send fee reminders');
  }
  
  return response.data.data;
};

export const getFeeReminderHistory = async (): Promise<Record<string, Date>> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(300);
    
    const history: Record<string, Date> = {};
    
    MOCK_FEE_DEFAULTERS.forEach(defaulter => {
      if (defaulter.lastReminderSent && defaulter.classId) {
        const existingDate = history[defaulter.classId];
        const currentDate = new Date(defaulter.lastReminderSent);
        
        if (!existingDate || currentDate > existingDate) {
          history[defaulter.classId] = currentDate;
        }
      }
    });
    
    return history;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<{ success: boolean; data: any[]; pagination?: any }>(
    `${API_BASE_PATH}/fee-reminders/history`
  );
  
  if (!response.data.success) {
    throw new Error('Failed to fetch fee reminder history');
  }
  
  const history: Record<string, Date> = {};
  response.data.data?.forEach((item: any) => {
    if (item.classId && item.lastReminderSent) {
      history[item.classId] = new Date(item.lastReminderSent);
    }
  });
  
  return history;
};

// ============================================
// NOTIFICATION APIs
// ============================================

export const getNotifications = async (
  params?: GetNotificationsRequest
): Promise<GetNotificationsResponse> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   await delay(400);
    
  //   let filteredNotifications = [...MOCK_NOTIFICATIONS];
    
  //   if (params?.type) {
  //     filteredNotifications = filteredNotifications.filter(n => n.type === params.type);
  //   }
    
  //   if (params?.isRead !== undefined) {
  //     filteredNotifications = filteredNotifications.filter(n => n.isRead === params.isRead);
  //   }
    
  //   filteredNotifications.sort((a, b) => 
  //     new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  //   );
    
  //   const page = params?.page || 1;
  //   const limit = params?.limit || 10;
  //   const startIndex = (page - 1) * limit;
  //   const endIndex = startIndex + limit;
    
  //   const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);
    
  //   return {
  //     notifications: paginatedNotifications,
  //     pagination: {
  //       currentPage: page,
  //       totalPages: Math.ceil(filteredNotifications.length / limit),
  //       totalCount: filteredNotifications.length,
  //       hasMore: endIndex < filteredNotifications.length,
  //     },
  //     unreadCount: getUnreadNotificationCount(),
  //   };
  // }
  
  // REAL API IMPLEMENTATION — remap 'limit' → 'pageSize' to match backend GetNotificationsQuery
  const { limit, ...rest } = params ?? {};
  const response = await axiosInstance.get<PaginatedResponse<Notification>>(
    `${API_BASE_PATH}/notifications`,
    { params: { ...rest, pageSize: limit } }
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to fetch notifications');
  }
  
  return {
    notifications: response.data.data || [],
    pagination: {
      currentPage: response.data.currentPage || 1,
      totalPages: response.data.totalPages || 1,
      totalCount: response.data.totalCount || 0,
      hasMore: response.data.hasNextPage || false,
    },
    unreadCount: 0,
  };
};

export const getUnreadCount = async (): Promise<UnreadCountResponse> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   await delay(100);
    
  //   return {
  //     unreadCount: getUnreadNotificationCount(),
  //     lastCheckedAt: new Date().toISOString(),
  //   };
  // }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<ApiResponse<{ unreadNotifications: number; unreadTotalMessages: number; unreadConversations: number }>>(
    `${API_BASE_PATH}/notifications/unread-count`
  );

  if (!response.data.succeeded) {
    throw new Error('Failed to fetch unread count');
  }

  return {
    unreadNotifications: response.data.data.unreadNotifications,
    unreadTotalMessages: response.data.data.unreadTotalMessages,
    unreadConversations: response.data.data.unreadConversations,
    lastCheckedAt: new Date().toISOString(),
  };
};

export const markNotificationAsRead = async (id: string): Promise<void> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   await delay(200);
    
  //   const notification = MOCK_NOTIFICATIONS.find(n => n.id === id);
  //   if (notification) {
  //     notification.isRead = true;
  //     notification.readAt = new Date().toISOString();
  //   }
  //   return;
  // }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.put<ApiResponse<number>>(
    `${API_BASE_PATH}/notifications/${id}/read`
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to mark notification as read');
  }
};

export const markAllNotificationsAsRead = async (): Promise<{ markedCount: number }> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   await delay(300);
    
  //   const unreadNotifications = MOCK_NOTIFICATIONS.filter(n => !n.isRead);
    
  //   unreadNotifications.forEach(n => {
  //     n.isRead = true;
  //     n.readAt = new Date().toISOString();
  //   });
    
  //   return { markedCount: unreadNotifications.length };
  // }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.put<ApiResponse<number>>(
    `${API_BASE_PATH}/notifications/mark-all-read`
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to mark all notifications as read');
  }
  
  return { markedCount: response.data.data };
};

export const deleteNotification = async (id: string): Promise<void> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(200);
    
    const index = MOCK_NOTIFICATIONS.findIndex(n => n.id === id);
    if (index > -1) {
      MOCK_NOTIFICATIONS.splice(index, 1);
    }
    return;
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.delete<ApiResponse<number>>(
    `${API_BASE_PATH}/notifications/${id}`
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to delete notification');
  }
};

export const clearAllNotifications = async (): Promise<{ clearedCount: number }> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(300);
    
    const readNotifications = MOCK_NOTIFICATIONS.filter(n => n.isRead);
    const clearedCount = readNotifications.length;
    
    MOCK_NOTIFICATIONS.splice(0, MOCK_NOTIFICATIONS.length, 
      ...MOCK_NOTIFICATIONS.filter(n => !n.isRead)
    );
    
    return { clearedCount };
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.delete<ApiResponse<number>>(
    `${API_BASE_PATH}/notifications/clear-all`
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to clear notifications');
  }
  
  return { clearedCount: response.data.data };
};

export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   await delay(200);
    
  //   return {
  //     userId: 'user_current',
  //     email: true,
  //     inApp: true,
  //     push: false,
  //     frequency: 'INSTANT',
  //     enabledTypes: {
  //       ANNOUNCEMENT: true,
  //       MESSAGE: true,
  //       FEE_REMINDER: true,
  //       APPROVAL: true,
  //       EVENT: true,
  //       SYSTEM: true,
  //     },
  //   };
  // }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<ApiResponse<NotificationPreferences>>(
    `${API_BASE_PATH}/notifications/preferences`
  );
  
  if (!response.data.succeeded || !response.data.data) {
    throw new Error('Failed to fetch notification preferences');
  }
  
  return response.data.data;
};

export const updateNotificationPreferences = async (
  data: Partial<NotificationPreferences>
): Promise<NotificationPreferences> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(300);
    
    const currentPrefs = await getNotificationPreferences();
    return { ...currentPrefs, ...data };
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.patch<{ 
    success: boolean; 
    data: NotificationPreferences;
    message?: string;
  }>(
    `${API_BASE_PATH}/notifications/preferences`,
    data
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error('Failed to update notification preferences');
  }
  
  return response.data.data;
};

// ============================================
// COMMUNICATION STATS & HISTORY
// ============================================

export const getCommunicationStats = async (): Promise<CommunicationStats> => {
  // MOCK IMPLEMENTATION
  if (USE_MOCK_API) {
    await delay(300);
    
    return {
      announcementsSent: MOCK_ANNOUNCEMENTS.length,
      messagesSent: MOCK_MESSAGES.length,
      feeRemindersSent: MOCK_FEE_REMINDERS.length,
      totalRecipients: 915,
      pendingFeeReminders: MOCK_FEE_DEFAULTERS.length,
      lastAnnouncementDate: MOCK_ANNOUNCEMENTS[0]?.createdAt,
      lastMessageDate: MOCK_MESSAGES[0]?.sentAt,
      lastFeeReminderDate: MOCK_FEE_REMINDERS[0]?.sentAt,
    };
  }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<ApiResponse<CommunicationStats>>(
    `${API_BASE_PATH}/announcements/stats`
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to fetch communication stats');
  }
  
  return response.data.data;
};

export const getCommunicationHistory = async (
  filters?: CommunicationFilters
): Promise<CommunicationHistory[]> => {
  // MOCK IMPLEMENTATION
  // if (USE_MOCK_API) {
  //   await delay(400);
    
  //   let filteredData = [...MOCK_COMMUNICATION_HISTORY];
    
  //   if (filters?.type) {
  //     filteredData = filteredData.filter(h => h.type === filters.type);
  //   }
    
  //   if (filters?.status) {
  //     filteredData = filteredData.filter(h => h.deliveryStatus === filters.status);
  //   }
    
  //   if (filters?.search) {
  //     const searchLower = filters.search.toLowerCase();
  //     filteredData = filteredData.filter(h =>
  //       h.title.toLowerCase().includes(searchLower) ||
  //       h.summary.toLowerCase().includes(searchLower)
  //     );
  //   }
    
  //   return filteredData;
  // }
  
  // REAL API IMPLEMENTATION
  const response = await axiosInstance.get<PaginatedResponse<CommunicationHistory>>(
    `${API_BASE_PATH}/announcements/history`,
    { params: filters }
  );
  
  if (!response.data.succeeded) {
    throw new Error('Failed to fetch communication history');
  }
  
  return response.data.data || [];
};

// ============================================
// DIRECT MESSAGING (CHAT) APIs
// ============================================

// In-memory store for mock mutations
const _mockConversations = [...MOCK_CONVERSATIONS];
const _mockMessages: Record<string, ChatMessage[]> = { ...MOCK_CHAT_MESSAGES };

export const getConversations = async (): Promise<Conversation[]> => {
  if (USE_MOCK_API) {
    await delay(300);
    return [..._mockConversations].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }
  const response = await axiosInstance.get<ApiResponse<Conversation[]>>(
    `${API_BASE_PATH}/conversations`
  );
  if (!response.data.succeeded) throw new Error('Failed to fetch conversations');
  return (response.data.data || []).map(c => ({ ...c, id: String(c.id) }));
};

export const startConversation = async (
  data: StartConversationRequest
): Promise<Conversation> => {
  if (USE_MOCK_API) {
    await delay(400);
    const existing = _mockConversations.find(
      c => c.participant.id === data.recipientId
    );
    if (existing) return existing;
    // Create new conversation stub — caller supplies participant info via recipientId
    const newConv: Conversation = {
      id: `conv_${Date.now()}`,
      participant: {
        id: data.recipientId,
        name: data.recipientId, // caller should pass name; here we stub
        role: 'STUDENT',
      },
      lastMessage: null,
      unreadCount: 0,
      updatedAt: new Date().toISOString(),
    };
    _mockConversations.unshift(newConv);
    _mockMessages[newConv.id] = [];
    return newConv;
  }
  const response = await axiosInstance.post<ApiResponse<Conversation>>(
    `${API_BASE_PATH}/conversations`,
    data
  );
  if (!response.data.succeeded) throw new Error('Failed to start conversation');
  return response.data.data!;
};

export const startConversationWithDetails = async (
  recipientId: string,
  name: string,
  role: 'STUDENT' | 'EMPLOYEE',
  className?: string,
  department?: string
): Promise<Conversation> => {
  if (USE_MOCK_API) {
    await delay(400);
    const existing = _mockConversations.find(
      c => c.participant.id === recipientId
    );
    if (existing) return existing;
    const newConv: Conversation = {
      id: `conv_${Date.now()}`,
      participant: { id: recipientId, name, role, className, department },
      lastMessage: null,
      unreadCount: 0,
      updatedAt: new Date().toISOString(),
    };
    _mockConversations.unshift(newConv);
    _mockMessages[newConv.id] = [];
    return newConv;
  }
  const response = await axiosInstance.post<ApiResponse<Conversation>>(
    `${API_BASE_PATH}/conversations`,
    { recipientId }
  );
  if (!response.data.succeeded) throw new Error('Failed to start conversation');
  const conv = response.data.data!;
  return { ...conv, id: String(conv.id) };
};

export const getConversationMessages = async (
  conversationId: string,
  lastMessageId?: number
): Promise<ChatMessagesResponse> => {
  if (USE_MOCK_API) {
    await delay(200);
    const messages = _mockMessages[conversationId] || [];
    return { messages, totalMessages: messages.length, isLast: true };
  }
  const params: Record<string, unknown> = { limit: 30 };
  if (lastMessageId != null) params.lastMessageId = lastMessageId;
  const response = await axiosInstance.get<ApiResponse<ChatMessagesResponse>>(
    `${API_BASE_PATH}/conversations/${conversationId}/messages`,
    { params }
  );
  if (!response.data.succeeded) throw new Error('Failed to fetch messages');
  const data = response.data.data!;
  return {
    ...data,
    messages: data.messages.map(m => ({ ...m, id: String(m.id), conversationId: String(m.conversationId) })),
  };
};

export const sendChatMessage = async (
  conversationId: string,
  data: SendChatMessageRequest
): Promise<ChatMessage> => {
  if (USE_MOCK_API) {
    await delay(250);
    const newMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      conversationId,
      content: data.content,
      sentBy: { id: 'user_current', name: 'Mr. Sharma' },
      sentAt: new Date().toISOString(),
      isRead: false,
      isMine: true,
    };
    if (!_mockMessages[conversationId]) _mockMessages[conversationId] = [];
    _mockMessages[conversationId].push(newMsg);
    // Update conversation last message
    const conv = _mockConversations.find(c => c.id === conversationId);
    if (conv) {
      conv.lastMessage = {
        content: data.content,
        sentAt: newMsg.sentAt,
        sentByMe: true,
      };
      conv.updatedAt = newMsg.sentAt;
    }
    return newMsg;
  }
  const response = await axiosInstance.post<ApiResponse<ChatMessage>>(
    `${API_BASE_PATH}/conversations/${conversationId}/messages`,
    data
  );
  if (!response.data.succeeded) throw new Error('Failed to send message');
  return response.data.data!;
};

export const markConversationRead = async (
  conversationId: string
): Promise<void> => {
  if (USE_MOCK_API) {
    await delay(100);
    const conv = _mockConversations.find(c => c.id === conversationId);
    if (conv) conv.unreadCount = 0;
    const msgs = _mockMessages[conversationId];
    if (msgs) msgs.forEach(m => { if (!m.isMine) m.isRead = true; });
    return;
  }
  await axiosInstance.patch(`${API_BASE_PATH}/conversations/${conversationId}/read`);
};

export const searchPeople = async (query: string): Promise<CommunicationPerson[]> => {
  if (USE_MOCK_API) {
    await delay(150);
    if (!query.trim()) return MOCK_PEOPLE;
    const q = query.toLowerCase();
    return MOCK_PEOPLE.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        p.className?.toLowerCase().includes(q) ||
        p.department?.toLowerCase().includes(q)
    );
  }
  // TODO: replace with real API when backend is ready
  // GET /v1/users/search?q=query
  const response = await axiosInstance.get<ApiResponse<CommunicationPerson[]>>(
    `${API_BASE_PATH}/users/search`,
    { params: { q: query } }
  );
  if (!response.data.succeeded) throw new Error('Failed to search people');
  return response.data.data || [];
};

// Export all services
export const communicationService = {
  // Announcements
  createAnnouncement,
  getAnnouncements,
  getAnnouncementById,

  // Messages
  sendMessage,
  getMessages,
  markMessageAsRead,

  // Fee Reminders
  getStudentsWithDues,
  sendFeeReminders,
  getFeeReminderHistory,

  // Notifications
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  clearAllNotifications,
  getNotificationPreferences,
  updateNotificationPreferences,

  // Stats & History
  getCommunicationStats,
  getCommunicationHistory,

  // Direct Messaging (Chat)
  getConversations,
  startConversation,
  startConversationWithDetails,
  getConversationMessages,
  sendChatMessage,
  markConversationRead,
  searchPeople,
};
